import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  FC,
  ReactNode,
} from "react";
import { adminLogin, adminMe, AdminUser } from "data/adminApi";

interface AuthContextValue {
  user: AdminUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(() => {
    try {
      const raw = localStorage.getItem("bcr_admin_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("bcr_admin_access");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await adminMe();
      setUser(me);
      localStorage.setItem("bcr_admin_user", JSON.stringify(me));
    } catch {
      localStorage.removeItem("bcr_admin_access");
      localStorage.removeItem("bcr_admin_refresh");
      localStorage.removeItem("bcr_admin_user");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (username: string, password: string) => {
    const data = await adminLogin(username, password);
    localStorage.setItem("bcr_admin_access", data.access);
    localStorage.setItem("bcr_admin_refresh", data.refresh);
    localStorage.setItem("bcr_admin_user", JSON.stringify(data.user));
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("bcr_admin_access");
    localStorage.removeItem("bcr_admin_refresh");
    localStorage.removeItem("bcr_admin_user");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
